import { Backdrop, CircularProgress } from "@mui/material";
import { FormikProps } from "formik";
import ATMLoadingButton from "src/components/UI/atoms/ATMLoadingButton/ATMLoadingButton";
import ATMRadioButton from "src/components/UI/atoms/ATMRadioButton/ATMRadioButton";
import ATMDatePicker from "src/components/UI/atoms/formFields/ATMDatePicker/ATMDatePicker";
import ATMMultiSelect from "src/components/UI/atoms/formFields/ATMMultiSelect/ATMMultiSelect";
import ATMSelect from "src/components/UI/atoms/formFields/ATMSelect/ATMSelect";
import useBatches from "src/hooks/useBatches";
import { RecordingRulesFormValues } from "src/models/RecordingRules.model";
import { useGetStudentByBatchIdQuery } from "src/services/RecordingRulesServices";

type Props = {
  formikProps: FormikProps<RecordingRulesFormValues>;
  onClose: () => void;
  formType?: "ADD" | "EDIT";
  isLoading?: boolean;
};
const groupOptions = [
  {
    value: "ALLOWED",
    label: "Allowed For ",
  },
  {
    value: "DENIED",
    label: "Denied For",
  },
];

const RecordingRulesForm = ({
  formikProps,
  onClose,
  formType,
  isLoading,
}: Props) => {
  const { values, setFieldValue, isSubmitting, handleBlur } = formikProps;
  const { batches, isDataLoading: isBatchesLoading } = useBatches();
  const { data: students, isLoading: isStudentsLoading } =
    useGetStudentByBatchIdQuery(values?.batchId?._id);
  if (isLoading) {
    return (
      <Backdrop open>
        {" "}
        <CircularProgress size={30} />{" "}
      </Backdrop>
    );
  }

  return (
    <div className="flex flex-col gap-6 p-4">
      <div className="flex items-center justify-between">
        <div className="text-xl font-medium">
          {" "}
          {formType === "EDIT"
            ? "Update Recording Rule"
            : "Add Recording Rule"}{" "}
        </div>
        <button type="button" className="hover:bg-hover" onClick={onClose}>
          Cancel
        </button>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {/* Batch Name */}
        <div className="col-span-2">
          <ATMSelect
            required
            name="batchId"
            value={values.batchId}
            onChange={(newValue) => {
              setFieldValue("batchId", newValue);
              setFieldValue("studentIds", "");
            }}
            onBlur={handleBlur}
            label="Batch"
            placeholder="Select Batch"
            options={batches}
            isLoading={isBatchesLoading}
            renderOption={(option, { context }) => {
              if (context === "menu") {
                return <div className="capitalize">{option?.batchName}</div>;
              } else {
                return <div className="capitalize">{option?.batchName}</div>;
              }
            }}
            isOptionEqualToValue={(option, selected) => {
              return option?._id === selected?._id;
            }}
            isSearchedOption={(option, searchValue) => {
              return searchValue
                ? option?.data?.batchName?.toLowerCase()?.includes(searchValue)
                : true;
            }}
          />
        </div>

        {/* studentIds */}
        <div className="col-span-2">
          <ATMMultiSelect
            required
            name="studentIds"
            value={values.studentIds}
            onChange={(newValue) => setFieldValue("studentIds", newValue)}
            onBlur={handleBlur}
            label="Students"
            placeholder="Select Students"
            options={students?.data?.map((student: any) => ({
              label: student?.name,
              value: student?._id,
            }))}
            isLoading={isStudentsLoading}
          />
        </div>
        <div>
          <ATMRadioButton
            name="radio"
            value={values?.radio}
            className="flex p-2 "
            options={groupOptions}
            onChange={(e) => {
              setFieldValue("radio", e);
              setFieldValue("always", true);
              setFieldValue("startDate", null);
              setFieldValue("endDate", null);
            }}
          />
        </div>

        {values.radio ? (
          <div className="flex items-center col-span-2 gap-2">
            <div
              onClick={() => {
                setFieldValue("always", true);
                setFieldValue("startDate", null);
                setFieldValue("endDate", null);
              }}
              className={`px-4 py-1 text-sm  capitalize ${
                values.always ? "bg-primary-dark text-white" : ""
              }  border cursor-pointer rounded-lg`}
            >
              Always
            </div>
            <div
              onClick={() => setFieldValue("always", false)}
              className={`px-4 py-1 text-sm  capitalize ${
                !values.always ? "bg-primary-dark  text-white" : ""
              }  border cursor-pointer rounded-lg`}
            >
              Range
            </div>
          </div>
        ) : null}

        {/* Allowled Date */}
        {!values.always && values.radio ? (
          <div className="flex items-center justify-between col-span-2">
            <div className="">
              <ATMDatePicker
                required
                name="startDate"
                value={values.startDate}
                onChange={(newValue) => setFieldValue("startDate", newValue)}
                label="Start Date"
              />
            </div>
            <div className="">
              <ATMDatePicker
                required
                name="endDate"
                value={values.endDate}
                onChange={(newValue) => setFieldValue("endDate", newValue)}
                label="End Date"
              />
            </div>
          </div>
        ) : null}
      </div>
      <div>
        <ATMLoadingButton isLoading={isSubmitting} type="submit">
          Save
        </ATMLoadingButton>
      </div>
    </div>
  );
};

export default RecordingRulesForm;
